import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { setJGRadioCue } from './hitLabRadioCue';
import {
  getActiveRadioTracks,
  mergeRadioCatalogItems,
} from './hitLabRadioTracks';
import { serviceCopy, EXAMPLE_TRACK_ID } from './hitLabServiceTerms';
import HitLabListenResult from './HitLabListenResult';
import useHitLabResultSharingCapability from './useHitLabResultSharingCapability';
import useHitLabOfferExposure from './useHitLabOfferExposure';
import joePortrait from './assets/joe-performing.jpg';
import faithCover from './assets/faith-cover.jpg';
import { EXAMPLE_SHARE_ID, resultShareUrl } from './HitLabSharedResult';
import './hitLabPaidOffer.css';
import './hitLabResultShare.css';
import { artistOpportunityCopy } from './hitLabArtistOpportunityCopy';
import { openRadioControls } from './radioParticipationIntent';
export default function HitLabPaidOffer({
  onCheckout,
  onEditProfile,
  busy,
  basePrice,
  plusPrice,
  plusAvailable,
  intakeId,
  authConfig,
  error,
  canceled,
}) {
  const { sharingAvailable, exampleFeedback, exampleError } =
    useHitLabResultSharingCapability();
  const editorialResult = {
    artistName: 'Uneekint',
    songTitle: 'I Got Faith (EDM Remix)',
    coverArtUrl: faithCover,
    reviewerName: 'Joe Galaxy',
    authoredFeedback: exampleFeedback.join('\n\n'),
  };
  const [exampleTrack, setExampleTrack] = useState(null);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [cueMessage, setCueMessage] = useState('');
  const priceRef = useRef(null),
    valueRef = useRef(null),
    ctaRef = useRef(null);
  useHitLabOfferExposure({
    intakeId,
    authConfig,
    refs: [priceRef, valueRef, ctaRef],
  });
  useEffect(() => {
    let active = true;
    axiosConn
      .get('hitlab/radio/catalog')
      .then((response) => {
        if (active)
          setExampleTrack(
            getActiveRadioTracks(
              mergeRadioCatalogItems(response?.data?.data?.items),
            ).find(
              (track) => (track.id || track.trackId) === EXAMPLE_TRACK_ID,
            ) || null,
          );
      })
      .catch(() => {
        if (active) setCatalogError(true);
      })
      .finally(() => {
        if (active) setCatalogLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <>
      <header className="site-header">
        <Link className="wordmark" to="/hitlab/intake">
          <span className="jg-mark">JG</span> HITLAB
        </Link>
        <span className="account">JG Artist</span>
      </header>
      <div className="offer-content">
        {canceled && (
          <p className="hitlab-alert" role="status">
            Checkout canceled. Your artist profile is saved. Continue when
            you’re ready.
          </p>
        )}
        {error && (
          <p className="hitlab-alert hitlab-alert-error" role="alert">
            {error}
          </p>
        )}
        <p className="sr-only" role="status">
          {cueMessage}
        </p>

        <div className="hero">
          <div className="hero-intro">
            <div className="ready">
              <span>✓</span> Your JG Artist profile is ready.
            </div>
            <p className="eyebrow">One record. A personal listen.</p>
            <h1>
              Got a record that belongs on <em>JG Radio?</em>
            </h1>
            <p className="hero-sub">
              Put it in JG HitLab. Joe Galaxy personally listens and gives you
              his written feedback, a selection decision and your next move.
              If your record earns rotation, listeners can discover your music,
              give feedback and support it.
            </p>
            <div className="reviewer-lockup">
              <div className="joe-portrait">
                <img src={joePortrait} alt="Joe Galaxy" />
              </div>
              <div>
                <strong>Personally reviewed by Joe Galaxy.</strong>
                <span>HitLab reviewer · JG Radio curator</span>
              </div>
            </div>
            {sharingAvailable && (
              <p className="share-value">
                <strong>Your result. Your choice to share.</strong> Keep Joe’s feedback
                private, or share your Listen Result through a public page
                featuring your record and his words—with a social preview ready
                to share.
              </p>
            )}
            <a className="quiet-link hero-proof-link" href="#proof">
              See a real Listen Result ↘
            </a>
          </div>
          <section className="offer-card" aria-labelledby="offer-title">
            <div className="offer-top">
              <span className="eyebrow">Your next move</span>
              <span>01 SONG</span>
            </div>
            <div className="offer-title-row">
              <h2 id="offer-title">
                Your JG HitLab
                <br />
                Listen Result
              </h2>
              <div className="price" ref={priceRef}>
                <strong>{basePrice}</strong>
                <span>One-time payment</span>
              </div>
            </div>
            <ul className="inclusions" ref={valueRef}>
              <li>
                <i>✓</i>
                <span>
                  <b>Joe Galaxy personally listens</b> to your record.
                </span>
              </li>
              <li>
                <i>✓</i>
                <span>
                  <b>His written feedback</b> on what stands out and what needs
                  attention, where applicable.
                </span>
              </li>
              <li>
                <i>✓</i>
                <span>
                  <b>The selection decision</b> and a clear next step.
                </span>
              </li>
              <li>
                <i>✓</i>
                <span>Consideration for JG Radio rotation.</span>
              </li>
              {sharingAvailable && (
                <li>
                  <i>✓</i>
                  <span>Optional sharing of your released Listen Result.</span>
                </li>
              )}
            </ul>
            <div className="delivery">
              <span>▤</span>
              <p>
                <strong>{serviceCopy.resultTime}</strong>
              </p>
            </div>
            <p className="boundary">
              Payment covers Joe’s review and written result. JG Radio rotation is earned.
            </p>
            <button
              className="primary checkout-button"
              disabled={busy}
              onClick={() => onCheckout('hitlab_processing')}
              id="first-checkout"
              ref={ctaRef}
            >
              Put My Record in JG HitLab <span>↗</span>
            </button>
            <p className="checkout-meta">
              {basePrice} total · Secure Stripe checkout
              <br />
              Submit your song after payment
            </p>
            <a
              className="offer-prereqs"
              href="#prerequisites"
              onClick={() => {
                const panel = document.getElementById('prerequisites');
                if (panel) panel.open = true;
              }}
            >
              What you’ll need to submit ↓
            </a>
          </section>
        </div>
        <section
          className="radio-intake-invitation"
          aria-label="Explore JG Radio"
        >
          <h2>Hear where your record could land.</h2>
          <p>Discover independent artists already in JG Radio rotation. Hear a record. Show some love.</p>
          <button type="button" className="quiet-link" onClick={() => openRadioControls('intake')}>Explore JG Radio</button>
        </section>
        <section
          className="rotation-opportunity"
          aria-labelledby="rotation-opportunity-title"
        >
          <h2 id="rotation-opportunity-title">
            Get heard—and hear back.
          </h2>
          <p>
            If Joe selects your record, we’ll confirm the permissions, playable
            audio and artwork needed to put it into JG Radio rotation. Once live,
            your record becomes part of an interactive listening experience.
          </p>
          <div className="artist-opportunity-benefits">
            <div>
              <h3>An introduction to the community</h3>
              <p>{artistOpportunityCopy.introduction}</p>
            </div>
            <div>
              <h3>Discovery beyond release day</h3>
              <p>{artistOpportunityCopy.discovery}</p>
            </div>
            <div>
              <h3>Listener feedback. Real artist support.</h3>
              <p>{artistOpportunityCopy.participation}</p>
              <p>{artistOpportunityCopy.cashout}</p>
              <p className="artist-opportunity-note">{artistOpportunityCopy.qualification}</p>
            </div>
          </div>
          <p className="artist-opportunity-note">{artistOpportunityCopy.notifications}</p>
        </section>
        <section className="proof-section" id="proof">
          <div className="section-heading">
            <div>
              <p className="eyebrow">The work behind the promise</p>
              <h2>
                {sharingAvailable
                  ? 'See the result. See how it shares.'
                  : 'Here’s what a Listen Result feels like.'}
              </h2>
            </div>
            <span className="section-index">01 / THE RESULT</span>
          </div>
          <article className="proof-card">
            <div className="proof-identity">
              <div className="cover-wrap">
                <img
                  src={faithCover}
                  alt="I Got Faith (EDM Remix) by Uneekint — original artwork"
                  width="300"
                  height="300"
                />
              </div>
              <div className="record-info">
                <p className="eyebrow">Example Listen Result</p>
                <h3>
                  I Got Faith
                  <br />
                  <span>(EDM Remix)</span>
                </h3>
                <p className="artist">Uneekint</p>
                <p className="affiliation">1017 Brick Squad Records artist</p>
                <a
                  className="source-link"
                  href="https://s4story.com/story/1017-brick-squad-records-announces-the-signing-of-uneekint-and-gepetto-jackson-s4story-10703904"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Label announcement ↗
                </a>
                <span className="rotation-status">
                  <i></i>{' '}
                  {catalogError
                    ? 'Current Radio availability could not be checked.'
                    : catalogLoaded
                    ? exampleTrack
                      ? 'Currently in JG Radio rotation'
                      : 'Not currently in JG Radio rotation'
                    : 'Checking current Radio availability…'}
                </span>
              </div>
            </div>
            <div className="proof-copy">
              <div className="reviewer-byline">
                <div className="joe-portrait small">
                  <img src={joePortrait} alt="" />
                </div>
                <span>From Joe Galaxy’s review</span>
              </div>
              {exampleFeedback[1] ? (
                <blockquote>“{exampleFeedback[1]}”</blockquote>
              ) : (
                <p>
                  {exampleError
                    ? 'The example feedback is temporarily unavailable.'
                    : 'Loading Joe’s example feedback…'}
                </p>
              )}
              <p className="example-context">
                Original feedback for a record that earned rotation. Current
                Radio availability is shown separately.
              </p>
              <button
                className="text-button"
                disabled={!exampleTrack}
                onClick={() => {
                  setJGRadioCue({
                    trackId: EXAMPLE_TRACK_ID,
                    source: 'hitlab_paid_offer',
                  });
                  setCueMessage(
                    'Song cued in Radio. Press Play when you’re ready.',
                  );
                }}
              >
                <span className="play-outline">▶</span> Listen to the song
              </button>
            </div>
            {sharingAvailable && (
              <div className="example-share-proof">
                <details>
                  <summary>Preview how this result shares</summary>
                  <img
                    src={`${resultShareUrl(EXAMPLE_SHARE_ID)}/og.png`}
                    alt="I Got Faith by Uneekint: artwork and Joe’s exact review excerpt in a social preview"
                  />
                  <p className="share-note">
                    The example uses Joe’s real feedback. Your result has its
                    own decision and next step. Sharing is always your choice.
                  </p>
                  <Link
                    to={{
                      pathname: `/share/hitlab-result/${EXAMPLE_SHARE_ID}`,
                      state: { fromHitlabOffer: true },
                    }}
                  >
                    Open the example’s full share page →
                  </Link>
                </details>
              </div>
            )}
            <details className="full-result" id="complete-result">
              <summary>
                View the complete Listen Result <span>+</span>
              </summary>
              <div id="result-mount">
                <HitLabListenResult
                  result={editorialResult}
                  editorial
                  catalogAvailable={!!exampleTrack}
                  catalogUnknown={catalogError || !catalogLoaded}
                />
              </div>
            </details>
          </article>
        </section>
        <section className="process-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">From your record to your result</p>
              <h2>One clear path forward.</h2>
            </div>
          </div>
          <ol className="process">
            <li>
              <span>01</span>
              <h3>Purchase your review.</h3>
              <p>One song. One-time payment.</p>
            </li>
            <li>
              <span>02</span>
              <h3>Submit your record.</h3>
              <p>Add your song link and rights confirmations.</p>
            </li>
            <li>
              <span>03</span>
              <h3>Joe Galaxy listens.</h3>
              <p>A personal review of your record.</p>
            </li>
            <li>
              <span>04</span>
              <h3>Get your Listen Result.</h3>
              <p>Written feedback, a decision and your next step.</p>
            </li>
          </ol>
          <div className="repeat-purchase">
            <button
              className="primary checkout-button"
              disabled={busy}
              onClick={() => onCheckout('hitlab_processing')}
              id="second-checkout"
            >
              Put My Record in JG HitLab <span>↗</span>
            </button>
            <p>
              {basePrice} total · Secure Stripe checkout · One song
              <br />
              <span>{serviceCopy.resultTime}</span>
            </p>
          </div>
        </section>
        <section className="questions-plus">
          <div className="questions">
            <p className="eyebrow">Before you put it in the Lab</p>
            <h2>A few good questions.</h2>
            <details>
              <summary>
                What happens if Joe selects my record?<span>+</span>
              </summary>
              <p>
                We confirm the Radio permissions, playable audio and artwork
                needed for rotation. Once your record is live on JG Radio,
                you’ll have its song link to share.
              </p>
            </details>
            <details>
              <summary>
                Who listens to my record?<span>+</span>
              </summary>
              <p>
                Joe Galaxy personally listens and writes your JG HitLab Listen
                Result.
              </p>
            </details>
            <details>
              <summary>
                What if my song isn’t selected?<span>+</span>
              </summary>
              <p>
                You still get Joe’s honest feedback, the reason for his decision
                and a clear next step. Your Listen Result is yours to keep
                {sharingAvailable ? '—and sharing it is your choice.' : '.'}
              </p>
            </details>
            <details>
              <summary>
                When will I receive my result?<span>+</span>
              </summary>
              <p>{serviceCopy.resultTime}</p>
              <p>{serviceCopy.clockRule}</p>
            </details>
            <details id="prerequisites">
              <summary>
                What do I need to submit?<span>+</span>
              </summary>
              <p>
                Your song title, genre, city or region, and a private HTTPS song
                link that opens without an access request. Have your existing
                rights confirmations ready. Phone is optional.
              </p>
              <p>
                Payment comes first; you submit the song and rights
                confirmations afterward.
              </p>
            </details>
          </div>
          {plusAvailable && (
            <aside className="plus-card">
              <p className="eyebrow">For the advertising opportunity</p>
              <h2>HitLab Plus</h2>
              <p className="plus-price">
                {plusPrice} <span>total package</span>
              </p>
              <p>
                Everything in your base review, plus eligibility for six months
                of JG Universe song advertising.
              </p>
              <ul>
                <li>
                  Catalog eligibility and approved artwork are required before
                  ad activation.
                </li>
                <li>The six-month term starts when the ad activates.</li>
                <li>
                  Song advertising is separate from earned Radio rotation.
                </li>
              </ul>
              <div className="plus-guarantee">
                <span className="eyebrow">Your review stays yours</span>
                <p>{serviceCopy.plusRefund}</p>
              </div>
              <details className="plus-terms">
                <summary>
                  Activation &amp; refund terms<span>+</span>
                </summary>
                <p>{serviceCopy.activation}</p>
                <p>{serviceCopy.refundTiming}</p>
                <p>{serviceCopy.materials}</p>
              </details>
              <button
                className="secondary checkout-button"
                disabled={busy || !plusAvailable}
                onClick={() => onCheckout('hitlab_plus_song_ad_rotation')}
              >
                Choose HitLab Plus ↗
              </button>
              <p className="plus-note">
                One package purchase · {plusPrice} total
              </p>
            </aside>
          )}
        </section>
        <footer className="page-footer">
          <span className="footer-brand">JG HITLAB</span>
          <div>
            <button className="quiet-link" onClick={onEditProfile}>
              Edit artist profile
            </button>
            <Link className="quiet-link" to="/home">
              Explore JG Universe ↗
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
}
